/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useCallback, useRef } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { MenuIdSearch } from '@jira/navigation-apps__constants/src/MenuIdSearch.tsx';
import { useIsInitialLoadLegacyIssueCreate } from '@jira/platform__legacy-create-issue-legacy/src/controllers/is-initial-load-create-issue-legacy/index.tsx';
import onCreateClick from '@jira/platform__legacy-create-issue-legacy/src/index.tsx';
import { KEYBOARD_SHORTCUT } from '@jira/platform__legacy-create-issue-legacy/src/types.tsx';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
} from '@jira/platform__help-panel-common-constants/src/index.tsx';
import { KeyboardShortcuts as LazyKeyboardShortcuts } from '@jira/platform__keyboard-shortcuts/src/index.tsx';
import {
	useRightSidebarController,
	useActiveRightSidebarState,
} from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { useTopMenus } from '@jira/navigation-apps__common/src/controllers/top-menus/index.tsx';
import { GENERIC_KEYBOARD_SHORTCUT } from '@jira/issue-create__use-trigger-issue-create-modal/src/constants.tsx';
import { useTriggerIssueCreateModal } from '@jira/issue-create__use-trigger-issue-create-modal/src/main.tsx';
import { BackgroundPriority } from '@jira/platform__services-non-critical/src/BackgroundPriority.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';

// Retaining the old name to avoid breaking any dashboards that may break due to this refactor
const PACKAGE_NAME = '@jira/navigation-apps__atlassian-navigation' as const;
const SERVICEDESK_QUEUES_CUSTOM_VIEW_ROUTE = 'servicedesk-queues-custom-view' as const;
const CSM_QUEUES_CUSTOM_VIEW_ROUTE = 'csm-queues-custom-view' as const;

export const useKeyboardShortcutsRouteName: () => string = createRouterSelector(
	(state) => state.route.name,
);

const KeyboardShortcuts = (): React.JSX.Element => {
	const routeName = useKeyboardShortcutsRouteName();
	const [, { on }] = useTopMenus(MenuIdSearch);
	const [, { off: closeAllMenus }] = useTopMenus();
	const { openRightSidebar, closeCurrentRightSidebar } = useRightSidebarController();
	const rightSidebarState = useActiveRightSidebarState();
	const [{ isInitialLoad }, actions] = useIsInitialLoadLegacyIssueCreate();
	const [, { openIssueCreateModal }] = useTriggerIssueCreateModal();
	const isInitialLoadRef = useRef<boolean>(false);
	isInitialLoadRef.current = isInitialLoad;

	const openSearch = useCallback(
		() =>
			requestAnimationFrame(() => {
				on();
			}),
		[on],
	);

	const openIssueCreate = useCallback(() => {
		if (fg('render_modern_global_issue_create_modal_experience')) {
			openIssueCreateModal({
				triggerPointKey: GENERIC_KEYBOARD_SHORTCUT,
			});
		} else {
			onCreateClick({
				isInitialLoad: isInitialLoadRef.current,
				triggerSource: KEYBOARD_SHORTCUT,
			});
			if (isInitialLoadRef.current) {
				actions.clearInitialLoaded();
			}
		}
	}, [actions, openIssueCreateModal]);

	const toggleHelpPanel = useCallback(() => {
		closeAllMenus();
		if (!rightSidebarState?.isCollapsed && rightSidebarState?.panelId === HELP_PANEL_ID) {
			closeCurrentRightSidebar();
		} else {
			openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH);
		}
	}, [
		closeAllMenus,
		closeCurrentRightSidebar,
		openRightSidebar,
		rightSidebarState?.isCollapsed,
		rightSidebarState?.panelId,
	]);

	const shortcuts = (
		<Placeholder name="lazy-shortcuts" fallback={null}>
			<LazyKeyboardShortcuts
				showIssueCreate={openIssueCreate}
				showSearch={openSearch}
				toggleHelpPanel={toggleHelpPanel}
				shouldUseHelpShortcut
			/>
		</Placeholder>
	);
	const queuesCustomViewRoutes: string[] = [
		SERVICEDESK_QUEUES_CUSTOM_VIEW_ROUTE,
		...(fg('csm_appification_standalone_navigation') ? [CSM_QUEUES_CUSTOM_VIEW_ROUTE] : []),
	];
	const shouldDeferShortcuts =
		queuesCustomViewRoutes.includes(routeName) && fg('jsm-queues-ttai-batch-1');

	return (
		<JSErrorBoundary
			id="integrations.keyboard-shortcuts"
			packageName={PACKAGE_NAME}
			fallback="unmount"
		>
			{shouldDeferShortcuts ? <BackgroundPriority>{shortcuts}</BackgroundPriority> : shortcuts}
		</JSErrorBoundary>
	);
};

const DummyComponent = () => null;

// https://jplat.atlassian.net/browse/JFP-3418
// This component currently crashes the page during Hot Module Replacement. So
// we're disabling this component when HMR is enabled (i.e. the --hmr flag is
// present). This a temporary measure until we find a proper fix. This will not
// affect regular development or the customer experience.
const exportedComponent: (() => null) | (() => React.JSX.Element) = process.env
	.HMR_ENABLED_HATCH_DO_NOT_USE
	? DummyComponent
	: KeyboardShortcuts;

export { exportedComponent as KeyboardShortcuts };
